import { DISMISS, useNotification } from "collections";
import { DeleteModal, useModalDispatch } from "collections/Modal";
import { useDeleteResourceFilesMutation } from "generated/graphql";
import { useTenantId } from "polyrouter/SelectedTenantProvider";
import { useTranslation } from "utilities/i18n";

const DeleteResourceFilesModal: React.FC<{
  onFinish: () => void;
  resourceFiles: Array<string>;
}> = ({ resourceFiles, onFinish }) => {
  const { t } = useTranslation();
  const [, deleteResourceFiles] = useDeleteResourceFilesMutation();
  const [addNotification] = useNotification();
  const tenantId = useTenantId();
  const closeModal = useModalDispatch();

  const removeResourceFiles = async () => {
    const { data, error } = await deleteResourceFiles({
      tenantId,
      fileNames: resourceFiles
    });

    if (
      error ||
      data?.deleteResourceFiles?.errors?.length ||
      !data?.deleteResourceFiles?.status
    ) {
      const filesNotDeleted =
        data?.deleteResourceFiles?.errors
          ?.map(error => {
            const [, fileName] = error?.split(" ") ?? [];
            return fileName;
          })
          .filter(fileName => !!fileName) ?? [];

      const message = filesNotDeleted.length
        ? `${t(
            "ERROR_REMOVING_RESOURCE_FILES_SPECIFIC"
          )}\n\n${filesNotDeleted.join("\n")}`
        : t("ERROR_REMOVING_RESOURCE_FILES");
      addNotification({
        type: "error",
        dismiss: DISMISS.DEFAULT,
        message,
        persistThroughNav: true
      });
    } else {
      addNotification({
        type: "success",
        dismiss: DISMISS.DEFAULT,
        message: t("SUCCESSFULLY_REMOVED_RESOURCE_FILES"),
        persistThroughNav: true
      });
    }

    onFinish();
    closeModal();
  };

  return (
    <DeleteModal
      name="DeleteResourceFiles"
      title={t("REMOVE_RESOURCE_FILE", { count: resourceFiles.length })}
      confirmationString={t("CONFIRM_REMOVE_RESOURCE_FILES", {
        count: resourceFiles.length
      })}
      action={"REMOVE"}
      onDelete={removeResourceFiles}
    >
      <div className="small-text overflow-list modal-body pt-1 pl-1">
        {resourceFiles.map(resourceFile => (
          <p key={resourceFile} className="list-item">
            {resourceFile}
          </p>
        ))}
      </div>
    </DeleteModal>
  );
};

export default DeleteResourceFilesModal;
